// apps/admin/src/pages/whitelist/tabs/WhitelistPaymentMethodsTab.tsx
//
// Which payment methods a whitelisted merchant can actually be charged on.
//
// Read-only, and short on purpose: the answer today is "Stripe cards, if the
// gateway is enabled". The value is in saying WHY the other method is absent
// — an operator who has just enabled the whitelist and cannot find PayPal
// needs to know it is not a misconfiguration on their side.
//
// Stripe's availability is read from the platform gateway settings rather
// than restated here, so this tab cannot drift from Gateway Settings.

import type { JSX } from 'react';
import { useQuery } from '@tanstack/react-query';

import { apiClient, ApiError } from '../../../lib/apiClient.js';
import { useTranslation } from '../../../i18n/useTranslation.js';

interface GatewaySettingsResponse {
  enabled?: boolean;
}

export function WhitelistPaymentMethodsTab(): JSX.Element {
  const { t } = useTranslation('finance');

  const stripeQuery = useQuery<GatewaySettingsResponse, ApiError>({
    queryKey: ['admin', 'gateway-settings', 'stripe'],
    queryFn: () =>
      apiClient.get<GatewaySettingsResponse>('/admin/gateway-settings/stripe'),
    staleTime: 60_000,
    // A failed read must not claim the gateway is ON — the merchant-facing
    // consequence of that lie is a payment option that always refuses.
    retry: false,
  });

  // THREE states, not two. A failed read is not a reading.
  //
  // `data?.enabled === true` collapses "off" and "could not tell" into the
  // same answer, and this screen states that answer as fact: an operator
  // reading "Gateway disabled — enable it in Gateway Settings first" goes and
  // changes a platform setting that was never off. The request can fail for
  // reasons that have nothing to do with the gateway — a 403 on an operator
  // without gateway-settings access being the likely one here.
  const stripeState: 'on' | 'off' | 'unknown' = stripeQuery.isError
    ? 'unknown'
    : stripeQuery.data?.enabled === true
      ? 'on'
      : 'off';

  return (
    <div className="space-y-4" data-testid="whitelist-payment-methods-tab">
      <div>
        <h2 className="text-sm font-semibold text-pw-text">
          {t('whitelist.paymentMethods.heading')}
        </h2>
        <p className="mt-1 text-xs text-pw-text-muted">
          {t('whitelist.paymentMethods.hint')}
        </p>
      </div>

      <ul className="space-y-2">
        <li
          className="flex items-center justify-between rounded border border-pw-border bg-pw-surface px-4 py-3"
          data-testid="whitelist-method-stripe"
        >
          <span className="text-sm text-pw-text">
            {t('whitelist.paymentMethods.stripe')}
          </span>
          <span
            className={
              stripeState === 'on'
                ? 'text-xs text-green-600 dark:text-green-300'
                : stripeState === 'unknown'
                  ? 'text-xs text-red-600 dark:text-red-300'
                  : 'text-xs text-pw-text-muted'
            }
            data-testid="whitelist-method-stripe-state"
            data-state={stripeQuery.isPending ? 'pending' : stripeState}
          >
            {stripeQuery.isPending
              ? '…'
              : stripeState === 'on'
                ? t('whitelist.paymentMethods.stripeOn')
                : stripeState === 'unknown'
                  ? t('whitelist.paymentMethods.stripeUnknown')
                  : t('whitelist.paymentMethods.stripeOff')}
          </span>
        </li>

        <li
          className="flex items-center justify-between rounded border border-pw-border bg-pw-surface px-4 py-3 opacity-60"
          data-testid="whitelist-method-paypal"
        >
          <span className="text-sm text-pw-text">
            {t('whitelist.paymentMethods.paypal')}
          </span>
          <span className="text-xs text-pw-text-muted">
            {t('whitelist.paymentMethods.paypalUnsupported')}
          </span>
        </li>
      </ul>
    </div>
  );
}

export default WhitelistPaymentMethodsTab;
